import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin } from "lucide-react";
import { api } from "../../App";
import { styledToast as toast } from "@/components/ui/sonner";

export default function OfficialLoginForm({ formData, setFormData, t }) {
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [loginMethod, setLoginMethod] = useState("password");
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [displayOtp, setDisplayOtp] = useState("");

  const handleSendOTP = async () => {
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length !== 10) {
      toast.error(t('validation.phoneInvalid'));
      return { success: false };
    }
    
    setLoading(true);
    try {
      const response = await api.post("/auth/send-otp", {
        phone: phoneDigits,
        role: "official",
        department: selectedDepartment,
      });
      setOtpSent(true);
      toast.success(`${t('common.otpSentTo')} ${phoneDigits}`);
      if (response.data.otp) {
        setDisplayOtp(response.data.otp);
      }
      return { success: true, waitForOtp: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.failedToSendOtp'));
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpValue) {
      toast.error(t('common.enterOtp'));
      return { success: false };
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/verify-otp", {
        phone: formData.phone,
        otp: otpValue,
      });
      return { 
        success: true, 
        token: response.data.access_token, 
        user: response.data.user,
        department: selectedDepartment
      };
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.invalidOtp'));
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setLoading(true);
    try {
      const response = await api.post("/auth/login", { 
        email: formData.email, 
        password: formData.password,
        department: selectedDepartment
      });
      return { 
        success: true, 
        token: response.data.access_token, 
        user: response.data.user,
        department: selectedDepartment,
        mustChangePassword: response.data.must_change_password || false
      };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(t('common.anErrorOccurred'));
      }
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDepartment) {
      toast.error(t('validation.departmentRequired'));
      return { success: false };
    }

    if (loginMethod === "otp" && selectedDepartment !== "gok") {
      if (!otpSent) {
        return handleSendOTP();
      } else {
        return handleVerifyOTP();
      }
    } else {
      return handlePasswordLogin();
    }
  };

  const getButtonText = () => {
    if (loading) return t('common.processing');
    if (loginMethod === "otp" && selectedDepartment !== "gok") {
      return otpSent ? t('login.verifyOTP') : t('login.sendOTP');
    }
    return t('login.signIn');
  };

  return {
    render: (
      <>
        {/* Department Selection */}
        <div className="mb-4">
          <Label className="text-sm font-medium text-emerald-800 mb-3 block">
            {t('login.selectDepartment')}
          </Label>
          <div className="grid grid-cols-3 gap-2">
            <Button
              type="button"
              variant={selectedDepartment === "revenue" ? "default" : "outline"}
              className={`h-auto min-h-[52px] py-2 px-1 flex flex-col items-center justify-center whitespace-normal ${
                selectedDepartment === "revenue"
                  ? "bg-emerald-800 hover:bg-emerald-900"
                  : "bg-white hover:bg-stone-50 border-2"
              }`}
              onClick={() => setSelectedDepartment("revenue")}
              data-testid="dept-revenue"
            >
              <Building2 className={`w-4 h-4 mb-0.5 flex-shrink-0 ${selectedDepartment === "revenue" ? "text-white" : "text-emerald-700"}`} />
              <span className={`text-[11px] leading-tight text-center ${selectedDepartment === "revenue" ? "text-white" : "text-emerald-700"}`}>{t('department.revenueShort')}</span>
            </Button>
            <Button
              type="button"
              variant={selectedDepartment === "survey" ? "default" : "outline"}
              className={`h-auto min-h-[52px] py-2 px-1 flex flex-col items-center justify-center whitespace-normal ${
                selectedDepartment === "survey"
                  ? "bg-blue-700 hover:bg-blue-800"
                  : "bg-white hover:bg-stone-50 border-2"
              }`}
              onClick={() => setSelectedDepartment("survey")}
              data-testid="dept-survey"
            >
              <MapPin className={`w-4 h-4 mb-0.5 flex-shrink-0 ${selectedDepartment === "survey" ? "text-white" : "text-emerald-700"}`} />
              <span className={`text-[11px] leading-tight text-center ${selectedDepartment === "survey" ? "text-white" : "text-emerald-700"}`}>{t('department.surveyShort')}</span>
            </Button>
            <Button
              type="button"
              variant={selectedDepartment === "gok" ? "default" : "outline"}
              className={`h-auto min-h-[52px] py-2 px-1 flex flex-col items-center justify-center whitespace-normal ${
                selectedDepartment === "gok"
                  ? "bg-amber-600 hover:bg-amber-700"
                  : "bg-white hover:bg-stone-50 border-2 border-amber-300"
              }`}
              onClick={() => setSelectedDepartment("gok")}
              data-testid="dept-gok"
            >
              <Building2 className={`w-4 h-4 mb-0.5 flex-shrink-0 ${selectedDepartment === "gok" ? "text-white" : "text-emerald-700"}`} />
              <span className={`text-[11px] leading-tight text-center ${selectedDepartment === "gok" ? "text-white" : "text-emerald-700"}`}>{t('department.gokShort')}</span>
            </Button>
          </div>
        </div>

        {/* Login Form - Only show after department selected */}
        {selectedDepartment && (
          <>
            {/* GOK indicator banner */}
            {selectedDepartment === "gok" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-amber-100 rounded-lg">
                    <Building2 className="w-4 h-4 text-amber-700" />
                  </div>
                  <span className="font-medium text-amber-800 text-sm">GOK {t('login.adminLogin')}</span>
                </div>
              </div>
            )}

            {/* Login Method Selection - Not for GOK */}
            {selectedDepartment !== "gok" && (
              <div className="mb-4">
                <Label className="text-sm font-medium text-emerald-800 mb-2 block">
                  {t('login.loginMethod')}
                </Label>
                <Select 
                  value={loginMethod} 
                  onValueChange={(value) => {
                    setLoginMethod(value);
                    setOtpSent(false);
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="login-method-select">
                    <SelectValue placeholder={t('login.selectLoginMethod')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="password" data-testid="login-method-password">
                      {t('login.emailPassword')}
                    </SelectItem>
                    <SelectItem value="otp" data-testid="login-method-otp">
                      {t('login.otp')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Password Login Form */}
            {(loginMethod === "password" || selectedDepartment === "gok") ? (
              <>
                <div>
                  <Label htmlFor="email">{t('login.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1.5"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="password">{t('login.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1.5"
                    data-testid="input-password"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="phone">{t('login.phone')}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1.5"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="input-phone"
                    disabled={otpSent}
                  />
                </div>
                {otpSent && (
                  <div>
                    {displayOtp && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-2 text-center" data-testid="otp-display-banner">
                        <p className="text-xs text-amber-700 font-medium mb-1">SMS gateway pending — OTP shown here for now</p>
                        <p className="text-2xl font-bold tracking-[0.3em] text-amber-900" data-testid="otp-display-code">{displayOtp}</p>
                      </div>
                    )}
                    <Label htmlFor="otp">{t('login.enterOTP')}</Label>
                    <Input
                      id="otp"
                      type="text"
                      required
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value)}
                      className="mt-1.5"
                      placeholder={t('login.otpPlaceholder')}
                      maxLength={6}
                      data-testid="input-otp"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setOtpSent(false);
                        setOtpValue("");
                        setDisplayOtp("");
                      }}
                      className="text-xs text-emerald-800 mt-1 hover:underline"
                    >
                      {t('login.changePhone')}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </>
    ),
    handleSubmit,
    getButtonText,
    loading,
    selectedDepartment
  };
}
